import { useEffect, RefObject } from 'react'
import { IAssigneeOptions } from 'appHooks/useGetBusinessProfile'

interface IUseModalEffectsProps {
  isOpen: boolean
  showFindTimeCalendar: boolean
  selectedTimezone: string
  highlightedGuestIndex: number
  startTime: string
  deviceTimezone: string
  timeGridRef: RefObject<HTMLDivElement>
  guestItemRefs: RefObject<(HTMLButtonElement | null)[]>
  temporarySlot: { date: Date; startHour: number; startMinute: number } | null
  isCalendarClickRef: RefObject<boolean>
  selectedDate: Date | null
  fetchedGuestsRef: RefObject<Set<string>>
  pendingGuestRequests: RefObject<Record<string, boolean>>
  setShowCalendar: (show: boolean) => void
  setShowStartTimePicker: (show: boolean) => void
  setShowEndTimePicker: (show: boolean) => void
  setShowFindTimeCalendar: (show: boolean) => void
  setShowGuestDropdown: (show: boolean) => void
  setGuestSearchQuery: (query: string) => void
  setSelectedGuests: (guests: IAssigneeOptions[]) => void
  setEmailError: (error: string) => void
  setSelectedDate: (date: Date | null) => void
  setStartTime: (time: string) => void
  setEndTime: (time: string) => void
  setSelectedTimezone: (timezone: string) => void
  setCurrentCalendarDate: (date: Date) => void
  setFindTimeCurrentDate: (date: Date) => void
  setFindTimeView: (view: 'Day' | '3 Days' | 'Week') => void
  setMeetingTitle: (title: string) => void
  setMeetingLink: (link: string) => void
  setMeetingDescription: (desc: string) => void
  setMeetingLocation: (location: string) => void
  setLinkCopied: (copied: boolean) => void
  setTemporarySlot: (slot: { date: Date; startHour: number; startMinute: number } | null) => void
  setSelectedAttendeeFilter: (filter: string[]) => void
}

export const useModalEffects = ({
  isOpen,
  showFindTimeCalendar,
  selectedTimezone,
  highlightedGuestIndex,
  startTime,
  deviceTimezone,
  timeGridRef,
  guestItemRefs,
  temporarySlot,
  isCalendarClickRef,
  selectedDate,
  fetchedGuestsRef,
  pendingGuestRequests,
  setShowCalendar,
  setShowStartTimePicker,
  setShowEndTimePicker,
  setShowFindTimeCalendar,
  setShowGuestDropdown,
  setGuestSearchQuery,
  setSelectedGuests,
  setEmailError,
  setSelectedDate,
  setStartTime,
  setEndTime,
  setSelectedTimezone,
  setCurrentCalendarDate,
  setFindTimeCurrentDate,
  setFindTimeView,
  setMeetingTitle,
  setMeetingLink,
  setMeetingDescription,
  setMeetingLocation,
  setLinkCopied,
  setTemporarySlot,
  setSelectedAttendeeFilter,
}: IUseModalEffectsProps) => {
  // Auto-scroll to current time when find time calendar opens or timezone changes
  useEffect(() => {
    if (showFindTimeCalendar && timeGridRef.current) {
      setTimeout(() => {
        if (timeGridRef.current) {
          const now = new Date()

          try {
            const formatter = new Intl.DateTimeFormat('en-US', {
              timeZone: selectedTimezone,
              hour: '2-digit',
              minute: '2-digit',
              hour12: false,
            })

            const parts = formatter.formatToParts(now)
            const currentHour = parseInt(parts.find((p) => p.type === 'hour')?.value || '0')
            const currentMinutes = parseInt(parts.find((p) => p.type === 'minute')?.value || '0')

            const rowHeight = 64
            const exactTimePosition = (currentHour + currentMinutes / 60) * rowHeight
            const scrollPosition =
              exactTimePosition - timeGridRef.current.clientHeight / 2 + rowHeight / 2
            timeGridRef.current.scrollTop = Math.max(0, scrollPosition)
          } catch (error) {
            const currentHour = now.getHours()
            const currentMinutes = now.getMinutes()
            const rowHeight = 64
            const exactTimePosition = (currentHour + currentMinutes / 60) * rowHeight
            const scrollPosition =
              exactTimePosition - timeGridRef.current.clientHeight / 2 + rowHeight / 2
            timeGridRef.current.scrollTop = Math.max(0, scrollPosition)
          }
        }
      }, 100)
    }
  }, [showFindTimeCalendar, selectedTimezone, timeGridRef])

  // Close all dropdowns and reset fields when modal closes
  useEffect(() => {
    if (!isOpen) {
      setShowCalendar(false)
      setShowStartTimePicker(false)
      setShowEndTimePicker(false)
      setShowFindTimeCalendar(false)
      setShowGuestDropdown(false)
      setGuestSearchQuery('')
      setSelectedGuests([])
      setEmailError('')
      setSelectedDate(null)
      setStartTime('')
      setEndTime('')
      setSelectedTimezone(deviceTimezone)
      setCurrentCalendarDate(new Date())
      setFindTimeCurrentDate(new Date())
      setFindTimeView('3 Days')
      fetchedGuestsRef.current?.clear()
      if (pendingGuestRequests.current) {
        Object.keys(pendingGuestRequests.current).forEach((key) => {
          delete pendingGuestRequests.current![key]
        })
      }
      setMeetingTitle('')
      setMeetingLink('')
      setMeetingDescription('')
      setMeetingLocation('')
      setLinkCopied(false)
    }
  }, [
    isOpen,
    deviceTimezone,
    setShowCalendar,
    setShowStartTimePicker,
    setShowEndTimePicker,
    setShowFindTimeCalendar,
    setShowGuestDropdown,
    setGuestSearchQuery,
    setSelectedGuests,
    setEmailError,
    setSelectedDate,
    setStartTime,
    setEndTime,
    setSelectedTimezone,
    setCurrentCalendarDate,
    setFindTimeCurrentDate,
    setFindTimeView,
    fetchedGuestsRef,
    pendingGuestRequests,
    setMeetingTitle,
    setMeetingLink,
    setMeetingDescription,
    setMeetingLocation,
    setLinkCopied,
  ])

  // Clear temporary slot when start time changes manually
  useEffect(() => {
    if (
      startTime &&
      temporarySlot &&
      isCalendarClickRef.current !== null &&
      !isCalendarClickRef.current
    ) {
      setTemporarySlot(null)
    }
    if (isCalendarClickRef.current !== null && isCalendarClickRef.current) {
      ;(isCalendarClickRef as any).current = false
    }
  }, [startTime, temporarySlot, isCalendarClickRef, setTemporarySlot])

  // Effect to scroll highlighted guest item into view
  useEffect(() => {
    if (
      highlightedGuestIndex >= 0 &&
      guestItemRefs.current &&
      guestItemRefs.current[highlightedGuestIndex]
    ) {
      guestItemRefs.current[highlightedGuestIndex]?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      })
    }
  }, [highlightedGuestIndex, guestItemRefs])

  // Reset attendee filter when Find Time calendar closes
  useEffect(() => {
    if (!showFindTimeCalendar) {
      setSelectedAttendeeFilter(['all'])
    }
  }, [showFindTimeCalendar, setSelectedAttendeeFilter])
}
